import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { GlobalsService } from '../../services/globals.service';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { AccountDetailModalComponent, AccountDetailModalData } from '../account-detail-modal/account-detail-modal.component';

interface TagBreakdownRow {
	tagref: number | null;
	tagdescription: string;
	balance_at_date: number;
}

interface TrialBalanceRow {
	accountcode: string;
	accountname: string;
	group: string;
	balance_at_date: number;
	tag_breakdown?: TagBreakdownRow[];
}

interface GroupedRows {
	group: string;
	rows: TrialBalanceRow[];
	subtotal: number;
}

@Component({
	selector: 'app-simple-trial-balance',
	templateUrl: './simple-trial-balance.component.html',
	styleUrls: ['./simple-trial-balance.component.scss']
})
export class SimpleTrialBalanceComponent implements OnInit {
	periods: any[] = [];
	formGroup: UntypedFormGroup;
	loading = false;
	exporting = false;
	trialBalanceList: TrialBalanceRow[] = [];
	groupedRows: GroupedRows[] = [];
	grandTotal = 0;
	color: string;
	hasRun = false;
	selectedPeriodLabel = '';
	sortCol: 'accountcode' | 'accountname' | 'balance_at_date' = 'accountcode';
	sortDir: 'asc' | 'desc' = 'asc';
	hideZero = true;

	constructor(
		private location: Location,
		private dialog: MatDialog,
		private globalsService: GlobalsService,
		private generalLedgerService: GeneralLedgerService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private fb: UntypedFormBuilder
	) {
		this.color = this.globalSearchService.getColor();
		this.formGroup = this.fb.group({
			period: [null, Validators.required]
		});
	}

	ngOnInit(): void {
		this.generalLedgerService.getPeriods().subscribe((results: any) => {
			this.periods = results.periods || [];
			const defaultTo = results.default_toperiod;
			if (defaultTo != null && this.periods.length) {
				this.formGroup.patchValue({ period: defaultTo });
				this.updatePeriodLabel(defaultTo);
			}
		});
	}

	back(): void {
		this.location.back();
	}

	onPeriodChange(): void {
		const val = this.formGroup.get('period')?.value;
		this.updatePeriodLabel(val);
		this.trialBalanceList = [];
		this.groupedRows = [];
		this.hasRun = false;
	}

	updatePeriodLabel(periodno: any): void {
		const match = this.periods.find(p => p.periodno == periodno);
		this.selectedPeriodLabel = match ? match.lastdate_in_period : '';
	}

	loadTrialBalance(): void {
		const period = this.formGroup.get('period')?.value;
		if (period == null) return;
		this.loading = true;
		this.generalLedgerService.getSimpleTrialBalance(Number(period)).subscribe({
			next: (data: any) => {
				this.trialBalanceList = Array.isArray(data) ? data : [];
				this.buildGroups();
				this.loading = false;
				this.hasRun = true;
			},
			error: () => {
				this.trialBalanceList = [];
				this.groupedRows = [];
				this.grandTotal = 0;
				this.loading = false;
				this.hasRun = true;
			}
		});
	}

	buildGroups(): void {
		const map = new Map<string, TrialBalanceRow[]>();
		for (const row of this.trialBalanceList) {
			if (this.hideZero && row.balance_at_date === 0) continue;
			if (!map.has(row.group)) map.set(row.group, []);
			map.get(row.group)!.push(row);
		}
		this.groupedRows = [];
		this.grandTotal = 0;
		map.forEach((rows, group) => {
			const subtotal = rows.reduce((s, r) => s + r.balance_at_date, 0);
			this.groupedRows.push({ group, rows, subtotal });
			this.grandTotal += subtotal;
		});
		this.groupedRows.sort((a, b) => a.group.localeCompare(b.group));
		this.applySortToGroups();
	}

	toggleHideZero(): void {
		this.hideZero = !this.hideZero;
		this.buildGroups();
	}

	sort(col: 'accountcode' | 'accountname' | 'balance_at_date'): void {
		if (this.sortCol === col) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortCol = col;
			this.sortDir = 'asc';
		}
		this.applySortToGroups();
	}

	applySortToGroups(): void {
		const col = this.sortCol;
		const dir = this.sortDir === 'asc' ? 1 : -1;
		for (const grp of this.groupedRows) {
			grp.rows.sort((a, b) => {
				const av = a[col];
				const bv = b[col];
				if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
				return String(av).localeCompare(String(bv)) * dir;
			});
		}
	}

	visibleTagBreakdown(row: TrialBalanceRow): TagBreakdownRow[] {
		const b = row.tag_breakdown;
		if (!b?.length) {
			return [];
		}
		if (!this.hideZero) {
			return b;
		}
		return b.filter(t => t.balance_at_date !== 0);
	}

	openAccount(row: TrialBalanceRow): void {
		const period = this.formGroup.get('period')?.value;
		const modalData: AccountDetailModalData = {
			accountcode: row.accountcode,
			accountname: row.accountname,
			periodno: period,
			periodLabel: this.selectedPeriodLabel
		};
		const ref = this.dialog.open(AccountDetailModalComponent, {
			width: '1000px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: modalData
		});
		ref.beforeClosed().subscribe(() => {
			if (ref.componentInstance?.parentShouldRefreshLists) {
				this.loadTrialBalance();
			}
		});
	}

	exportExcel(): void {
		const data: { 'Account Code': string; 'Account Name': string; 'Group': string; Tag: string; Balance: number }[] = [];
		for (const r of this.trialBalanceList) {
			if (this.hideZero && r.balance_at_date === 0) {
				continue;
			}
			data.push({
				'Account Code': r.accountcode,
				'Account Name': r.accountname,
				'Group': r.group,
				Tag: '',
				Balance: r.balance_at_date
			});
			const tags = this.visibleTagBreakdown(r);
			for (const t of tags) {
				data.push({
					'Account Code': r.accountcode,
					'Account Name': r.accountname,
					'Group': r.group,
					Tag: t.tagdescription,
					Balance: t.balance_at_date
				});
			}
		}
		const period = this.formGroup.get('period')?.value ?? '';
		this.globalSearchService.exportJsonToExcel(data, `SimpleTrialBalance_P${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
	}

	/**
	 * PDF is rendered server-side (wkhtmltopdf) with only report header CSS.
	 * innerHTML would omit encapsulated styles and mat-icon ligatures show as raw text.
	 */
	private escPdf(v: unknown): string {
		return String(v ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	private formatPdfBalance(n: number): string {
		return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
	}

	private buildPdfTableHtml(): string {
		const esc = (v: unknown) => this.escPdf(v);
		const balTd = (n: number) =>
			`<td class="stb-pdf-bal" style="text-align:right;font-family:monospace,monospace;${
				n < 0 ? 'color:#c62828;' : ''
			}">${esc(this.formatPdfBalance(n))}</td>`;

		const style = `<style type="text/css">
			table.stb-pdf-table { width:100%; border-collapse:collapse; font-size:11px; color:#000; margin-top:8px; }
			table.stb-pdf-table th, table.stb-pdf-table td { padding:6px 8px; border:1px solid #ccc; vertical-align:top; }
			table.stb-pdf-table thead th { background:#e9ecef; font-weight:bold; text-align:left; }
			table.stb-pdf-table thead th.stb-pdf-bal { text-align:right; }
			table.stb-pdf-table tr.stb-pdf-grp td { background:#f1f3f5; font-weight:bold; }
			table.stb-pdf-table tr.stb-pdf-tag td { background:#fafbfc; font-size:10px; }
			table.stb-pdf-table tr.stb-pdf-sub td { background:#eef1f4; font-weight:600; }
			table.stb-pdf-table tr.stb-pdf-grand td { background:#e2e6ea; font-weight:700; }
			table.stb-pdf-table th.stb-pdf-code, table.stb-pdf-table td.stb-pdf-code { font-family:monospace,monospace; width:11%; }
			table.stb-pdf-table th.stb-pdf-name, table.stb-pdf-table td.stb-pdf-name { width:42%; }
			table.stb-pdf-table th.stb-pdf-tagc, table.stb-pdf-table td.stb-pdf-tagc { width:22%; }
		</style>`;

		let rows = '';
		for (const grp of this.groupedRows) {
			rows += `<tr class="stb-pdf-grp"><td colspan="4">${esc(grp.group)}</td></tr>`;
			for (const row of grp.rows) {
				rows += `<tr>
					<td class="stb-pdf-code">${esc(row.accountcode)}</td>
					<td class="stb-pdf-name">${esc(row.accountname)}</td>
					<td class="stb-pdf-tagc"></td>
					${balTd(row.balance_at_date)}
				</tr>`;
				for (const tb of this.visibleTagBreakdown(row)) {
					rows += `<tr class="stb-pdf-tag">
						<td class="stb-pdf-code"></td>
						<td class="stb-pdf-name"></td>
						<td class="stb-pdf-tagc">${esc(tb.tagdescription)}</td>
						${balTd(tb.balance_at_date)}
					</tr>`;
				}
			}
			rows += `<tr class="stb-pdf-sub">
				<td class="stb-pdf-code"></td>
				<td class="stb-pdf-name" style="text-align:right;font-weight:600;">${esc(grp.group)} Total</td>
				<td class="stb-pdf-tagc"></td>
				${balTd(grp.subtotal)}
			</tr>`;
		}

		const foot = `<tfoot><tr class="stb-pdf-grand">
			<td class="stb-pdf-code"></td>
			<td class="stb-pdf-name" style="text-align:right;font-weight:700;">Grand Total</td>
			<td class="stb-pdf-tagc"></td>
			${balTd(this.grandTotal)}
		</tr></tfoot>`;

		const head = `<thead><tr>
			<th class="stb-pdf-code">Account</th>
			<th class="stb-pdf-name">Name</th>
			<th class="stb-pdf-tagc">Tag</th>
			<th class="stb-pdf-bal">Balance</th>
		</tr></thead>`;

		return `${style}<table class="table table-bordered stb-pdf-table">${head}<tbody>${rows}</tbody>${foot}</table>`;
	}

	exportPdf(): void {
		if (!this.trialBalanceList.length) return;
		this.exporting = true;
		const period = this.formGroup.get('period')?.value ?? '';
		const encoded: string = this.globalSearchService.base64encode(this.buildPdfTableHtml());
		const data = {
			content: encoded,
			filename: `SimpleTrialBalance_P${period}_${new Date().toISOString().slice(0, 10)}`,
			title: `Simple Trial Balance — Period ending: ${this.selectedPeriodLabel}`,
			landscape: true
		};
		this.printService.pdf(data).subscribe(
			(result: any) => {
				this.exporting = false;
				if (result && result.content) {
					this.globalSearchService.downloadPdf(result.content, data.filename);
				}
			},
			() => { this.exporting = false; }
		);
	}
}
